import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import {Field, getFormValues, reduxForm} from 'redux-form';
import * as validateRule from '../../validateRule';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {connect} from 'react-redux';

import LoginCommon, {checkInputEmpty, commonStyles, renderTextField} from './loginCommon';
import BackButton from '../../atoms/Buttons/BackButton';
import {PositiveButton} from '../../atoms/Buttons/PositiveButton';
import {commonOperations} from '../../../reducks/common/index';
import {authOperations} from '../../../reducks/auth/index';
import withRouter from '../../organisms/withRouter';

/**
 * ログイン画面:パスワード変更 テンプレート。
 * 画面ID:SCZ1001
 */
class Container extends React.Component {
  /**
   * 変更ボタンクリック時処理
   */
  doPasswordChange = async () => {
    const {doShowMessage} = this.props;

    // Valdationが動作していない事があるため、手動でチェックする
    if (!checkInputEmpty(this.createCheckTargets, doShowMessage)) {
      return;
    }

    const dialog = {
      message: {
        id: 'TKSCS00002C',
        values: ['パスワードを変更'],
      },
      action: this._execPasswordChange,
    };

    doShowMessage(dialog);
  };

  /**
   * 手動で未入力チェックを行う対象の入力項目情報を生成。
   *
   * @return {{name: string, value: string}[]} 入力項目情報
   */
  createCheckTargets = () => {
    const {
      values,
    } = this.props;

    // チェックする情報
    const checkTargets = [
      {name: '「現在のパスワード」', value: values.currentPassword},
      {name: '「新しいパスワード」', value: values.newPassword},
      {name: '「新しいパスワード(確認)」', value: values.newPasswordConfirm},
    ];

    return checkTargets;
  };

  /**
   * パスワード変更処理実施
   */
  _execPasswordChange = async () => {
    const {
      doChangePassword,
      doCompletePassword,
      doLogout,
      doShowMessage,
      loginInfo,
      navigate,
      values,
    } = this.props;

    const newPassword = values.newPassword;

    let result;
    const signInUser = loginInfo.signInUser;
    if (signInUser) {
      // 仮パスワードでログイン時は新パスワードを設定
      result = await doCompletePassword(signInUser, newPassword);
    } else {
      // パスワード変更を実行
      result = await doChangePassword(values.currentPassword, newPassword);
    }

    switch (result) {
      // パスワード変更成功
      case 'OK':
        doShowMessage({
          message: 'TKSCZ00020I',
          action: () => {
            doLogout(navigate);
          },
        });
        break;

      case 'PasswordHistoryPolicyViolationException':
        doShowMessage({
          message: 'TKSCZ00057E',
        });
        break;

      // パスワード要件エラー、システムエラー、その他のエラー
      case 'InvalidPasswordException':
      case 'SYS_ERROR':
      default:
        doShowMessage({
          message: 'TKSCS00001S',
          action: () => {
            doLogout(navigate);
          },
        });
        break;
    }
  };

  /**
   * 描画処理。
   * @return {JSX.Element} メニュー画面の要素
   */
  render() {
    const {classes, handleSubmit, loginInfo} = this.props;
    const userId = loginInfo.userId;

    return (
      <LoginCommon pageTitle="パスワード変更">
        <Grid container>
          <Grid item xs={12}>
            <Field
              name="currentPassword"
              label="現在のパスワード"
              type="password"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              autoComplete="username"
              validate={[
                validateRule.required,
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              name="newPassword"
              label="新しいパスワード"
              type="password"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              autoComplete="current-password"
              validate={[
                validateRule.required,
                validateRule.minLength(8),
                validateRule.maxLength(20),
                validateRule.password(userId),
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.typography}>
              <Typography>{'※8文字以上20文字以下の半角英数字記号であること'}</Typography>
              <Typography>{'※英字、数字、記号をすべて含むこと'}</Typography>
              <Typography>{'※ユーザIDを含まないこと'}</Typography>
              <Typography>{'※同一文字の4回以上連続を含まないこと'}</Typography>
              <Typography>{'※前回と同じパスワードは使用不可'}</Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Field
              name="newPasswordConfirm"
              label="新しいパスワード(確認)"
              type="password"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              autoComplete="current-password"
              validate={[
                validateRule.required,
              ]}
            />
          </Grid>

          <Grid container xs={12} className={classes.button}>
            <Grid item xs={1} sm={2}/>
            <Grid item xs={5} sm={4} style={{marginLeft: -4, marginRight: 4}}>
              <PositiveButton
                onClick={handleSubmit(this.doPasswordChange)}
                variant="contained"
                size="large"
                fullWidth
                style={{minWidth: 0}}
              >
                <span>変更</span>
              </PositiveButton>
            </Grid>
            <Grid item xs={5} sm={4} style={{marginLeft: 4, marginRight: -4}}>
              <BackButton
                props={this.props}
                fullWidth
                style={{minWidth: 0}}
              />
            </Grid>
            <Grid item xs={1} sm={2}/>
          </Grid>
        </Grid>
      </LoginCommon>
    );
  }
}

/**
 * 複数項目のバリデーション。
 *
 * @param {object} values フォームの値
 * @return {object} エラー情報
 */
const validate = (values) => {
  const errors = {};

  const newPassword = values.newPassword;
  const newPasswordConfirm = values.newPasswordConfirm;

  // 新しいパスワード(確認)が未入力の場合は終了
  if (newPasswordConfirm == null) {
    return errors;
  }

  // パスワードと確認が一致しない場合
  if (newPassword !== newPasswordConfirm) {
    errors['newPasswordConfirm'] = 'パスワードが一致しません';
  }

  return errors;
};

const FORM_NAME = 'ChangePassword';

Container.propTypes = {
  classes: PropTypes.object.isRequired,
  doChangePassword: PropTypes.func,
  doCompletePassword: PropTypes.func,
  doLogout: PropTypes.func,
  doShowMessage: PropTypes.func,
  handleSubmit: PropTypes.func,
  loginInfo: PropTypes.object,
  values: PropTypes.object,
};

const mapDispatchToProps = {
  doChangePassword: authOperations.doChangePassword,
  doCompletePassword: authOperations.doCompletePassword,
  doLogout: authOperations.doLogoutOperation,
  doShowMessage: commonOperations.doShowMessage,
};

const mapStateToProps = (state) => ({
  initialValues: {
    currentPassword: null,
    newPassword: null,
    newPasswordConfirm: null,
  },
  loginInfo: state.auth.loginInfo,
});

Container = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
  validate,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
  };
})(Container));
export default withStyles(commonStyles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withRouter(Container)),
);
